//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect, useLayoutEffect } from 'react';

// *** MUI / STYLE ***
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === API ===
import { mergeDocumentAtPageNumber, onImage, onImageFocus, removeListener, requestImage, resetImage, setMainImage, splitDocumentAtPageNumber, requestFirstFieldOfThePage, moveImage } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===
import { ThumbnailPlaceholder } from './ThumbnailPlaceholder';

//#endregion

//-----------------------------------------------------------------
export function Thumbnail({ id, index, id_doc }) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();
  const ref = React.useRef();

  const [ image       , setImage    ] = useState( null  );
  const [ is_selected , setSelected ] = useState( false );
  const has_image = !!image;

  const url = image ? image.url : '';

  const [is_drag      , setIsDrag     ] = useState(false);
  const [is_drop      , setIsDrop     ] = useState(false);
  const [is_drop_above, setIsDropAbove] = useState(false);

  let placeholder = <ThumbnailPlaceholder id={id} index={index} />;

  //#endregion

  //#region [STYLES]
 
  const _styles = {
    container_0: {
      'white-space' : 'nowrap'  ,
      overflow      : 'visible' ,
    },
    container_main: {
      'white-space' : 'nowrap'  ,
      overflow      : 'visible' ,
    },
    container: {
      borderColor    : 'transparent'   , /* remove the border's colour */
      boxShadow      : is_selected ? `0 0 0 5px ${theme.palette.primary.main}` : 0, /* emulate the border */
      display        : 'inline-block' ,
      justifyContent : 'center'       ,
      margin         : '1rem'         ,
      backgroundColor : is_drag ? '' : '',
      maxWidth       : '100px' ,
      maxHeight      : '125px' , 
    },
    thumbnail: {
      maxWidth       : '100px' ,
      maxHeight      : '125px' , 
      mixBlendMode   : is_drag ? 'multiply' : 'normal',
    },
  }

  const
    CONTAINER_DRAG = {
      sx  : _styles.container_0,
      ref : ref    ,
      key : id     ,
      id  : id     ,
      draggable   : true,
      onDragStart : onDragStart,
      onDragEnter : onDragEnter,
      onDragLeave : onDragLeave,
      onDragOver  : onDragOver ,
      onDragEnd   : onDragEnd  ,
      onDrop      : onDrop     ,
    },
    CONTAINER_MAIN = {
      sx: _styles.container_main,
    },
    CONTAINER = {
      sx            : _styles.container,
      tabIndex      : index,
      onClick       : () => setMainImage( image ),
      onKeyDown     : handleKeyDown,
      onDoubleClick : () => requestFirstFieldOfThePage(id),
    },
    THUMBNAIL = {
      name      : 'thumbnail_img',
      component : 'img',
      sx        : _styles.thumbnail,
      src       : url    ,
      alt       : ''     ,
      key       : id     ,
      loading   : 'lazy' ,
      selected  : is_selected,
      onLoad: (e)=>{
        if(is_selected) {
          ref.current.scrollIntoView({behavior: 'auto'});
        }
      },
      onError: (event) => {
        resetImage( id, image.s3 );
      }
    };

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onDragStart( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData( 'data/image', JSON.stringify( {id, index, id_doc} ) );
    setIsDrag( true );
  }

  //-----------------------------------------------------------------
  function onDragEnd( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    setIsDrag( false );
  }

  //-----------------------------------------------------------------
  function onDragEnter( event ) {
    //-----------------------------------------------------------------
    const is_image = event.dataTransfer.types.includes("data/image");
    if ( !is_image ) return;

    const image_data_string = event.dataTransfer.getData( "data/image" );
    const image_data = image_data_string ? JSON.parse( image_data_string ) : {};
    if( image_data.id == id ) return;
    
    event.stopPropagation();
    event.preventDefault();

    setIsDrop(true);
  }

  //-----------------------------------------------------------------
  function onDragLeave( event ) {
    //-----------------------------------------------------------------
    const is_image = event.dataTransfer.types.includes("data/image");
    if ( !is_image ) return;

    const image_data_string = event.dataTransfer.getData( "data/image" );
    const image_data = image_data_string ? JSON.parse( image_data_string ) : {};
    if( image_data.id == id ) return;

    event.stopPropagation();
    event.preventDefault();

    const rect = ref.current.getBoundingClientRect();
    if(event.clientY < rect.top || event.clientY >= rect.bottom || event.clientX < rect.left || event.clientX >= rect.right) {
      setIsDrop(false);
    }
  }

  //-----------------------------------------------------------------
  function onDragOver( event ) {
    //-----------------------------------------------------------------
    const is_image = event.dataTransfer.types.includes("data/image");
    if ( !is_image ) return;

    const image_data_string = event.dataTransfer.getData( "data/image" );
    const image_data = image_data_string ? JSON.parse( image_data_string ) : {};
    if( image_data.id == id ) return;
    
    event.stopPropagation();
    event.preventDefault();

    // const is_different_doc  = 
    const is_directly_above = index - image_data.index ===  1;
    const is_directly_below = index - image_data.index === -1;
    
    const is_same_doc = id_doc == image_data.id_doc;

    const bounding = ref.current.getBoundingClientRect()
    const offset_y = bounding.y + (bounding.height/2);
    const is_mouse_below = event.clientY - offset_y > 0;
    const is_mouse_above = event.clientY - offset_y < 0;

    if( is_same_doc && is_directly_above && is_mouse_above ) setIsDrop( false );
    if( is_same_doc && is_directly_below && is_mouse_below ) setIsDrop( false );

    if( is_mouse_below  &&  is_drop_above ) return setIsDropAbove( false );
    if( is_mouse_above  && !is_drop_above ) return setIsDropAbove(  true );
  }

  //-----------------------------------------------------------------
  function onDrop( event ) {
    //-----------------------------------------------------------------
    const is_image = event.dataTransfer.types.includes("data/image");
    if ( !is_image ) return;

    const image_data_string = event.dataTransfer.getData( "data/image" );
    const image_data = image_data_string ? JSON.parse( image_data_string ) : {};
    if( image_data.id == id ) return;

    event.stopPropagation();
    event.preventDefault();

    setIsDrop(false);
    moveImage( image_data.id, id, is_drop_above );
  }

  //-----------------------------------------------------------------
  function handleKeyDown(e) {
    //-----------------------------------------------------------------
    if(is_selected) {
      const is_split = e.key === 's';
      const is_merge = e.key === 'm';
           if( is_split ) splitDocumentAtPageNumber( id );
      else if( is_merge ) mergeDocumentAtPageNumber( id );
    }
  }

  //#endregion

  //#region [EFFECT]

  useLayoutEffect( () => {
    if(is_selected && ref.current) {
      ref.current.scrollIntoView({behavior: 'auto', block: 'nearest'});
    }
  }, [is_selected] );


  useEffect( () => {
    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      setImage(data)
    }
    //-----------------------------------------------------------------
    function handleImageFocus(data) {
      //-----------------------------------------------------------------
      const value = data.clear ? false : true;
      setSelected( value );
    }

    onImage     ( id , handleImage      );
    onImageFocus( id , handleImageFocus );

    requestImage( id );
    return () => {
      removeListener( 'IMAGE-'       + id, handleImage      );
      removeListener( 'IMAGE-FOCUS-' + id, handleImageFocus );
    }
  }, [] );

  //#endregion
  
  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box { ...CONTAINER_DRAG } >
        { is_drop &&   is_drop_above ? placeholder : null }
        { displayMain() }
        { is_drop &&  !is_drop_above ? placeholder : null }
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displayMain() {
    //-----------------------------------------------------------------
    return has_image ? (
      <Box { ...CONTAINER_MAIN } >
        <Box> Page: { image.page_number } </Box>
        <Box { ...CONTAINER } >
          <Box { ...THUMBNAIL } />
        </Box>
      </Box>
    ) : (
      <Box>Loading...</Box>
    )
  }

  //#endregion

  return display();
};
